import type { SurveySubmission } from '@/features/submit-survey';
import { SURVEY_OTHER, SURVEY_SECTIONS, type SurveyChoiceKey } from '@/shared/config';

/** 「기타」 칸이 딸린 고르는 문항. 4번(AI 경험)에는 기타가 없다. */
type OtherKey = Exclude<SurveyChoiceKey, 'aiExperience'>;

/**
 * 작성 중인 설문 한 벌. 제출 전까지 화면이 쥐고 있는 값이다.
 *
 * 문항마다 `useState` 를 두지 않고 한 객체로 둔다 — 진행률·빠진 문항 검사가 전체를 한꺼번에
 * 보기 때문이다. 바꿀 때는 늘 새 객체를 만든다.
 */
export interface SurveyDraft {
  role: string;
  timeSpent: readonly string[];
  tools: readonly string[];
  aiExperience: string;
  wantCases: readonly string[];
  reason: string;
  /** 「기타」를 고른 문항에 적은 내용. 고르지 않은 문항의 내용은 보내지 않는다. */
  other: Readonly<Record<OtherKey, string>>;
  story: string;
  email: string;
  /** 9번 — 아직 고르지 않았으면 빈 값. */
  consulting: '' | 'yes' | 'no';
  agreedToPrivacy: boolean;
  marketingOptIn: boolean;
}

export const EMPTY_DRAFT: SurveyDraft = {
  role: '',
  timeSpent: [],
  tools: [],
  aiExperience: '',
  wantCases: [],
  reason: '',
  other: { role: '', timeSpent: '', tools: '', wantCases: '', reason: '' },
  story: '',
  email: '',
  consulting: '',
  agreedToPrivacy: false,
  marketingOptIn: false,
};

/** 화면의 문항 칸 이름. 빠진 문항을 짚고 그 자리로 옮겨 갈 때 쓴다. */
export type SurveyFieldKey = SurveyChoiceKey | 'story' | 'email' | 'consulting' | 'agreedToPrivacy';

export interface SurveyProblem {
  key: SurveyFieldKey;
  message: string;
  /**
   * 고칠 자리. `answer` 는 문항 자체(보기·입력칸), `other` 는 「기타」 내용 칸이다.
   * 기타를 이미 골랐는데 내용만 비었을 때 보기로 초점을 보내면, 고칠 칸을 사용자가 다시 찾아야 한다.
   */
  target: 'answer' | 'other';
}

/**
 * 진행률의 분모 — **필수** 문항 수(1·2·4~9번). 동의는 문항이 아니라 세지 않는다.
 *
 * 선택 문항(3번)을 분모에 넣으면, 그걸 건너뛴 사람은 낼 수 있는 상태인데도 막대가 89%에서 멈춘다.
 */
export const SURVEY_REQUIRED_COUNT = 8;

/** 서버(zod `z.email`)만큼 엄격하지 않다 — 오타를 먼저 잡아 주는 용도이고, 최종 판단은 서버가 한다. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CHOICE_QUESTIONS = SURVEY_SECTIONS.flatMap((section) => section.questions);

const picked = (draft: SurveyDraft, key: SurveyChoiceKey): string | readonly string[] => draft[key];

/** 하나 고르는 문항은 빈 문자열이, 여러 개 고르는 문항은 빈 목록이 「답 없음」이다. */
const hasAnswer = (value: string | readonly string[]): boolean => value.length > 0;

const picksOther = (value: string | readonly string[]): boolean =>
  typeof value === 'string' ? value === SURVEY_OTHER : value.includes(SURVEY_OTHER);

const hasOtherKey = (key: SurveyChoiceKey): key is OtherKey => key !== 'aiExperience';

/** 「기타」를 골랐는데 내용을 비웠는지. 「기타」로 끝나면 아무 정보도 아니다. */
const missesOther = (draft: SurveyDraft, key: SurveyChoiceKey): boolean =>
  hasOtherKey(key) && picksOther(picked(draft, key)) && draft.other[key].trim().length === 0;

/**
 * 빠졌거나 잘못된 문항 — **화면 순서대로.** 첫 번째가 스크롤을 옮겨 갈 자리다.
 *
 * 서버도 같은 검사를 한다. 여기서 먼저 막는 이유는 한 번에 전부 알려 주기 위해서다 —
 * 서버는 첫 실패에서 돌아오므로, 빠진 곳이 셋이면 세 번 제출해야 다 알게 된다.
 */
export function findProblems(draft: SurveyDraft): SurveyProblem[] {
  const choiceProblems = CHOICE_QUESTIONS.flatMap((question): SurveyProblem[] => {
    if (question.required && !hasAnswer(picked(draft, question.key))) {
      return [{ key: question.key, message: '보기를 골라 주세요', target: 'answer' }];
    }
    if (missesOther(draft, question.key)) {
      return [{ key: question.key, message: '「기타」 내용을 적어 주세요', target: 'other' }];
    }
    return [];
  });

  const answerProblem = (key: SurveyFieldKey, message: string): SurveyProblem[] => [
    { key, message, target: 'answer' },
  ];
  const email = draft.email.trim();
  const rest: SurveyProblem[] = [
    ...(draft.story.trim().length === 0 ? answerProblem('story', '업무 이야기를 들려주세요') : []),
    ...(email.length === 0
      ? answerProblem('email', '이메일을 입력해 주세요')
      : EMAIL_PATTERN.test(email)
        ? []
        : answerProblem('email', '이메일 형식이 올바르지 않습니다')),
    ...(draft.consulting === '' ? answerProblem('consulting', '하나를 골라 주세요') : []),
    ...(draft.agreedToPrivacy
      ? []
      : answerProblem('agreedToPrivacy', '개인정보 수집·이용에 동의해 주세요')),
  ];

  return [...choiceProblems, ...rest];
}

/** 답한 필수 문항 수 — 진행률 막대에 쓴다. 분모(SURVEY_REQUIRED_COUNT)와 같은 기준이라야 100%에 닿는다. */
export function countAnswered(draft: SurveyDraft): number {
  const choices = CHOICE_QUESTIONS.filter(
    (question) => question.required && hasAnswer(picked(draft, question.key)),
  );
  const others = [
    draft.story.trim().length > 0,
    draft.email.trim().length > 0,
    draft.consulting !== '',
  ].filter(Boolean);
  return choices.length + others.length;
}

/** 「기타」를 고른 문항의 내용만 싣는다. 고르지 않은 문항의 내용은 무엇에 대한 답인지 모른다. */
const otherIfPicked = (draft: SurveyDraft, key: OtherKey): string | undefined => {
  const text = draft.other[key].trim();
  return picksOther(picked(draft, key)) && text.length > 0 ? text : undefined;
};

/** 서버가 받는 모양으로 옮긴다. 검사를 통과한 뒤에만 부른다. */
export function toSubmission(draft: SurveyDraft, homepage: string | undefined): SurveySubmission {
  return {
    answers: {
      role: draft.role,
      roleOther: otherIfPicked(draft, 'role'),
      timeSpent: [...draft.timeSpent],
      timeSpentOther: otherIfPicked(draft, 'timeSpent'),
      tools: [...draft.tools],
      toolsOther: otherIfPicked(draft, 'tools'),
      aiExperience: draft.aiExperience,
      wantCases: [...draft.wantCases],
      wantCasesOther: otherIfPicked(draft, 'wantCases'),
      reason: draft.reason,
      reasonOther: otherIfPicked(draft, 'reason'),
      story: draft.story.trim(),
    },
    email: draft.email.trim(),
    wantsConsulting: draft.consulting === 'yes',
    agreedToPrivacy: true,
    marketingOptIn: draft.marketingOptIn,
    homepage,
  };
}
